import {
  get, post, put, del,
} from './API';

/**
 * Service for interacting with the Dashboard Notification API
 */
const NotificationService = {
  /**
   * Get notifications for the current user
   * @param {Object} options - Query options
   * @param {number} [options.limit] - Number of notifications to fetch
   * @param {Object} [options.lastKey] - Pagination key
   * @param {boolean} [options.unreadOnly] - Only fetch unread notifications
   * @returns {Promise<{notifications: Array, lastKey: Object, count: number}>}
   */
  getNotifications: async (options = {}) => {
    const params = {};
    if (options.limit) params.limit = options.limit;
    if (options.lastKey) params.lastKey = JSON.stringify(options.lastKey);
    if (options.unreadOnly) params.unreadOnly = 'true';

    const response = await get('/notifications', params);
    return response.data?.data || response.data;
  },

  /**
   * Get unread notification count (optimized for polling)
   * @returns {Promise<{count: number}>}
   */
  getUnreadCount: async () => {
    const response = await get('/notifications/count');
    return response.data?.data || response.data;
  },

  /**
   * Mark notifications as read
   * @param {string[]} notificationIds - IDs of notifications to mark as read
   * @returns {Promise<{success: boolean}>}
   */
  markAsRead: async (notificationIds) => {
    const response = await post('/notifications/read', { notificationIds });
    return response.data?.data || response.data;
  },

  /**
   * Mark all notifications as read
   * @returns {Promise<{success: boolean}>}
   */
  markAllAsRead: async () => {
    const response = await post('/notifications/read-all', {});
    return response.data?.data || response.data;
  },

  /**
   * Get user notification preferences
   * @returns {Promise<Object>}
   */
  getPreferences: async () => {
    const response = await get('/notifications/preferences');
    return response.data?.data || response.data;
  },

  /**
   * Update user notification preferences
   * @param {Object} preferences - Updated preferences
   * @returns {Promise<Object>}
   */
  updatePreferences: async (preferences) => {
    const response = await put('/notifications/preferences', preferences);
    return response.data?.data || response.data;
  },

  /**
   * Get groups the current user belongs to
   * @returns {Promise<Array>}
   */
  getMyGroups: async () => {
    const response = await get('/notifications/my-groups');
    return response.data?.data || response.data;
  },

  // ==================== Admin - Group Management ====================

  /**
   * Get all notification groups (admin only)
   * @param {boolean} [includeInactive=false] - Include inactive groups
   * @returns {Promise<Array>}
   */
  getGroups: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: 'true' } : {};
    const response = await get('/notifications/groups', params);
    return response.data?.data || response.data;
  },

  /**
   * Create a notification group (admin only)
   * @param {string} name - Group name
   * @param {string} [description] - Group description
   * @returns {Promise<Object>}
   */
  createGroup: async (name, description = '') => {
    const response = await post('/notifications/groups', { name, description });
    return response.data?.data || response.data;
  },

  /**
   * Get a notification group by ID (admin only)
   * @param {string} groupId - Group ID
   * @returns {Promise<Object>}
   */
  getGroup: async (groupId) => {
    const response = await get(`/notifications/groups/${groupId}`);
    return response.data?.data || response.data;
  },

  /**
   * Update a notification group (admin only)
   * @param {string} groupId - Group ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>}
   */
  updateGroup: async (groupId, updates) => {
    const response = await put(`/notifications/groups/${groupId}`, updates);
    return response.data?.data || response.data;
  },

  /**
   * Deactivate a notification group (admin only)
   * @param {string} groupId - Group ID
   * @returns {Promise<{success: boolean}>}
   */
  deactivateGroup: async (groupId) => {
    const response = await del(`/notifications/groups/${groupId}`);
    return response.data?.data || response.data;
  },

  /**
   * Get members of a notification group (admin only)
   * @param {string} groupId - Group ID
   * @returns {Promise<Array>}
   */
  getGroupMembers: async (groupId) => {
    const response = await get(`/notifications/groups/${groupId}/members`);
    return response.data?.data || response.data;
  },

  /**
   * Add a member to a notification group (admin only)
   * @param {string} groupId - Group ID
   * @param {string} memberEmail - Email of the user to add
   * @returns {Promise<Object>}
   */
  addGroupMember: async (groupId, memberEmail) => {
    const response = await post(`/notifications/groups/${groupId}/members`, { memberEmail });
    return response.data?.data || response.data;
  },

  /**
   * Remove a member from a notification group (admin only)
   * @param {string} groupId - Group ID
   * @param {string} memberEmail - Email of the user to remove
   * @returns {Promise<{success: boolean}>}
   */
  removeGroupMember: async (groupId, memberEmail) => {
    const response = await del(`/notifications/groups/${groupId}/members/${encodeURIComponent(memberEmail)}`);
    return response.data?.data || response.data;
  },

  // ---- phone notifications ------------------------------------------------
  // These manage DEVICES, not what you are notified about. A push goes
  // exactly where a bell row goes, so the switches above are still the only
  // place that decides which types reach you.

  /**
   * The VAPID public key and the phones this user has registered.
   * `public_key: null` means push is not configured on this deploy, which is
   * what lets the settings screen say so instead of offering a dead switch.
   * @returns {Promise<{public_key: string|null, devices: Array}>}
   */
  getPushState: async () => {
    const response = await get('/notifications/push');
    return response.data?.data || response.data;
  },

  /**
   * Register this browser.
   * @param {{endpoint: string, keys: {p256dh: string, auth: string}}} subscription
   */
  subscribePush: async (subscription) => {
    const response = await post('/notifications/push', subscription);
    return response.data?.data || response.data;
  },

  /**
   * Forget one registered phone. A device that is not yours answers
   * `{ removed: false }` rather than 404.
   * @param {string} deviceId
   */
  unsubscribePush: async (deviceId) => {
    const response = await del(`/notifications/push/${encodeURIComponent(deviceId)}`);
    return response.data?.data || response.data;
  },
};

/**
 * The three calls `Services/PushNotifications` needs, for office staff.
 * Passing this rather than the whole service keeps that file unaware of
 * which surface it is running on.
 */
export const officePushApi = {
  getState: () => NotificationService.getPushState(),
  subscribe: (subscription) => NotificationService.subscribePush(subscription),
  unsubscribe: (deviceId) => NotificationService.unsubscribePush(deviceId),
};

export default NotificationService;
